import React from "react";
import * as Types from "../graphql/types";
import { useTheme } from "@govlaunch/web/src/theme/ThemeProvider";
import { Tooltip, Box, BoxProps } from "@govlaunch/web";

type TSponsorship = { __typename: "Sponsorship" } & Pick<Types.ISponsorship, "name" | "logo" | "link">;

export type Size =
  /**
   * The small size
   */
  | "2xs"
  /**
   * Not so small
   */
  | "xs"
  | "sm"
  | "md"
  | "lg"
  | "xl"
  | "2xl";

export const badgeSizes = {
  "2xs": 4,
  xs: 6,
  sm: 8,
  md: 12,
  lg: 16,
  xl: 24,
  "2xl": 32,
  full: "full",
};

export interface ISponsorshipBadgesProps {
  sponsorships: Array<TSponsorship>;
  size?: Size;
}

const Img = Box.withComponent("img");

export default function SponsorshipBadges({ sponsorships, size = "md" }: ISponsorshipBadgesProps) {
  const theme = useTheme();
  const sizeKey = size ? badgeSizes[size] : "md";
  // @ts-ignore
  const _size = theme.sizes[sizeKey];
  const badgeSize = parseFloat(_size) * 2;

  return (
    <div>
      {sponsorships.slice(0, 4).map((sponsorship, index) => {
        if (sponsorship.link) {
          return (
            <SponsorshipBadgeAnchor
              key={`${sponsorship.link}/${index}`}
              sponsorship={sponsorship}
              badgeSize={badgeSize}
              index={index}
            />
          );
        }

        return (
          <SponsorshipBadge sponsorship={sponsorship} badgeSize={badgeSize} index={index} key={sponsorship.name} />
        );
      })}
    </div>
  );
}

interface ISponsorshipBadgeProps {
  sponsorship: TSponsorship;
  badgeSize: number;
  index: number;
}

function SponsorshipBadgeAnchor({ sponsorship, badgeSize, index }: ISponsorshipBadgeProps) {
  return (
    <a href={sponsorship.link || ""} target="_blank" rel="noopener noreferrer" key={sponsorship.name}>
      <SponsorshipBadge sponsorship={sponsorship} badgeSize={badgeSize} index={index} />
    </a>
  );
}

function SponsorshipBadge({ sponsorship, badgeSize, index }: ISponsorshipBadgeProps) {
  return (
    <Box
      position="absolute"
      {...getPosition(index, badgeSize)}
      rounded="full"
      width={badgeSize}
      height={badgeSize}
      textAlign="center"
      _hover={{
        cursor: "pointer",
      }}
    >
      <Tooltip label={sponsorship.name} aria-label={sponsorship.name} color="black" bg="gray.100" placement="top">
        <Box rounded="full" size="100%" objectFit="cover" display="flex">
          {sponsorship.logo ? (
            <Badge logo={sponsorship.logo} />
          ) : (
            <BadgeName
              display="inline-flex"
              alignItems="center"
              justifyContent="center"
              bg="white"
              name={sponsorship.name}
              rounded="full"
              size="100%"
              objectFit="cover"
              _hover={{
                backgroundColor: "gray.100",
                border: "1px",
                borderColor: "gray.100",
              }}
            />
          )}
        </Box>
      </Tooltip>
    </Box>
  );
}

interface IBadgeProps {
  logo: string;
}

const Badge = ({ logo }: IBadgeProps) => {
  return (
    <Img
      as="img"
      size="100%"
      rounded="full"
      objectFit="cover"
      src={logo}
      bg="white"
      _hover={{
        backgroundColor: "gray.100",
        border: "1px",
        borderColor: "gray.100",
      }}
    />
  );
};

const getPosition = (index: number, size: number) => {
  if (index === 0) {
    return {
      bottom: -size * 2 + 4,
      right: -size * 2 + 4,
    };
  }

  if (index === 1) {
    return {
      top: -size * 2 + 4,
      left: -size * 2 + 4,
    };
  }

  if (index === 2) {
    return {
      bottom: -size * 2 + 4,
      left: -size * 2 + 4,
    };
  }

  return {
    top: -size * 2 + 4,
    right: -size * 2 + 4,
  };
};

const BadgeName = ({ name, ...props }: BoxProps & { name?: string }) => {
  return (
    <Box textAlign="center" textTransform="uppercase" fontWeight="medium" aria-label={name} {...props}>
      {name ? getInitials(name) : null}
    </Box>
  );
};

const getInitials = (name: string) => {
  const [firstName, lastName] = name.split(" ");

  if (firstName && lastName) {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`;
  }
  return firstName.charAt(0);
};
